import { useState, useCallback } from "react";
import DSBox from "../../system-design/DSBox";
import DSTextField from "../../system-design/DSTextField";
import DSTypography from "../../system-design/DSTypography";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import { AppHomeEntry, AppHomeEntryType } from "../../../api/appHomeEntry";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import ConfirmationSwitch from "../../ConfirmationSwitch";
import DSDialog from "../../system-design/DSDialog";
import DSSelect from "../../system-design/DSSelect";
import DSMenuItem from "../../system-design/DSMenuItem";
import DSFormControl from "../../system-design/DSFormControl";
import DSInputLabel from "../../system-design/DSInputLabel";
import uploadFile from "../../../utils/uploadFiles";
import useUpdateAppHomeEntryMutation from "../hooks/useUpdateAppHomeEntryMutation";
import useCreateAppHomeEntryMutation from "../hooks/useCreateAppHomeEntryMutation";

type AppHomeEntryDialogProps = {
  open: boolean;
  onClose: () => void;
  entry: AppHomeEntry | null;
};

const AppHomeEntryDialog = ({
  open,
  onClose,
  entry,
}: AppHomeEntryDialogProps) => {
  const isEditMode = !!entry;

  const [formData, setFormData] = useState<Partial<AppHomeEntry>>({
    type: entry?.type || AppHomeEntryType.KLYDO_QUERY,
    metadata: {
      query: entry?.metadata?.query || "",
      url: entry?.metadata?.url || "",
    },
    pictureUrl: entry?.pictureUrl || "",
    active: entry?.active || false,
  });

  const { mutate: updateAppHomeEntry, isPending: isUpdating } =
    useUpdateAppHomeEntryMutation({
      onSuccess: () => {
        onClose();
      },
    });

  const { mutate: createAppHomeEntry, isPending: isCreating } =
    useCreateAppHomeEntryMutation({
      onSuccess: () => {
        onClose();
      },
    });

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target?.files?.[0];
      if (file) {
        const fileReader = new FileReader();
        fileReader.onload = () => {
          uploadFile({
            file: fileReader.result as string,
            folder: "appHomeEntries",
            upload_preset: "Bezalel_preset",
          }).then((url) =>
            setFormData((prev) => ({
              ...prev,
              pictureUrl: url,
            })),
          );
        };
        fileReader.readAsDataURL(file);
      }
    },
    [],
  );

  const handleSubmit = () => {
    const metadata =
      formData.type === AppHomeEntryType.EXTERNAL_LINK
        ? { url: formData.metadata?.url }
        : { query: formData.metadata?.query };

    if (isEditMode && entry) {
      const updatedEntry: AppHomeEntry = {
        ...entry,
        type: formData.type!,
        metadata,
        pictureUrl: formData.pictureUrl!,
        active: formData.active!,
      };
      updateAppHomeEntry(updatedEntry);
    } else {
      const newEntry: Partial<AppHomeEntry> = {
        type: formData.type,
        metadata,
        pictureUrl: formData.pictureUrl,
        active: formData.active,
      };
      createAppHomeEntry(newEntry, {
        onSuccess: () => {
          onClose();
        },
      });
    }
  };

  const isFormValid = () => {
    if (!formData.pictureUrl) return false;

    if (formData.type === AppHomeEntryType.EXTERNAL_LINK) {
      return !!formData.metadata?.url;
    } else {
      return !!formData.metadata?.query;
    }
  };

  return (
    <DSDialog
      open={open}
      onClose={onClose}
      aria-labelledby="app-home-entry-title"
    >
      <DSBox sx={{ width: 500, bgcolor: "background.paper", borderRadius: 1 }}>
        <DSDialogTitle id="app-home-entry-title">
          {isEditMode ? "Edit" : "Add New"} App Home Entry
        </DSDialogTitle>

        <DSDialogContent>
          <DSBox display="flex" flexDirection="column" gap={3} mt={1}>
            {formData.pictureUrl && (
              <DSBox
                sx={{
                  width: "150px",
                  height: "150px",
                  borderRadius: "16px",
                  overflow: "hidden",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  position: "relative",
                }}
              >
                <img
                  src={formData.pictureUrl}
                  alt="Entry preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </DSBox>
            )}
            <DSFormControl fullWidth>
              <DSInputLabel id="entry-type-label">Entry Type</DSInputLabel>
              <DSSelect
                labelId="entry-type-label"
                value={formData.type}
                label="Entry Type"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    type: e.target.value as AppHomeEntryType,
                  }))
                }
              >
                <DSMenuItem value={AppHomeEntryType.KLYDO_QUERY}>
                  Klydo Query
                </DSMenuItem>
                <DSMenuItem value={AppHomeEntryType.ARTIST_QUERY}>
                  Artist Query
                </DSMenuItem>
                <DSMenuItem value={AppHomeEntryType.EXTERNAL_LINK}>
                  External Link
                </DSMenuItem>
              </DSSelect>
            </DSFormControl>

            {formData.type === AppHomeEntryType.EXTERNAL_LINK ? (
              <DSTextField
                fullWidth
                label="URL"
                value={formData.metadata?.url || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    metadata: {
                      ...prev.metadata,
                      url: e.target.value,
                    },
                  }))
                }
                placeholder="Enter external URL"
                required
              />
            ) : (
              <DSTextField
                fullWidth
                label="Query"
                value={formData.metadata?.query || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    metadata: {
                      ...prev.metadata,
                      query: e.target.value,
                    },
                  }))
                }
                placeholder={`Enter ${formData.type === AppHomeEntryType.KLYDO_QUERY ? "Klydo" : "Artist"} query`}
                required
              />
            )}

            <DSBox>
              <DSTextField
                type="file"
                fullWidth
                inputProps={{
                  accept: "image/*",
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                label="Entry Image"
                onChange={handleFileUpload}
                required
                helperText={
                  formData.pictureUrl
                    ? "Current image will be used if no new file is selected"
                    : ""
                }
              />
            </DSBox>

            <DSBox display="flex" alignItems="center">
              <DSTypography variant="body1" mr={1}>
                Active
              </DSTypography>
              <ConfirmationSwitch
                status={formData.active || false}
                action={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    active: value,
                  }))
                }
                onMessage="Are you sure you want to activate this entry?"
                offMessage="Are you sure you want to deactivate this entry?"
              />
            </DSBox>
          </DSBox>
        </DSDialogContent>

        <DSDialogActions>
          <DSSecondaryButton onClick={onClose}>Cancel</DSSecondaryButton>
          <DSPrimaryButton
            onClick={handleSubmit}
            disabled={!isFormValid() || isUpdating || isCreating}
          >
            {isEditMode
              ? isUpdating
                ? "Updating..."
                : "Update Entry"
              : isCreating
                ? "Creating..."
                : "Create Entry"}
          </DSPrimaryButton>
        </DSDialogActions>
      </DSBox>
    </DSDialog>
  );
};

export default AppHomeEntryDialog;
